import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MovieForm from './MovieForm';
import Modal from './Modal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { uploadPoster } from '../utils/imageUpload';
import { downloadCardImage } from '../utils/downloadCard';
import { tagAtPatchOnEdit, byTagAtOrCreatedAt } from '../utils/tagTimestamps';
import { availablePeople } from '../utils/people';
import { useLongPress } from '../hooks/useLongPress';
import { useAuth } from '../AuthContext';

const WATCHED_AT_FIELD = {
  Joy: 'watched_with_joy_at',
  Rick: 'watched_with_rick_at',
  Lisa: 'watched_with_lisa_at',
  Gionnan: 'watched_with_gionnan_at',
  Youssra: 'watched_with_youssra_at',
};

const WATCHED_WITH_FIELD = {
  Joy: 'watched_with_joy',
  Rick: 'watched_with_rick',
  Lisa: 'watched_with_lisa',
  Gionnan: 'watched_with_gionnan',
  Youssra: 'watched_with_youssra',
};

// A movie can be watched with different people on different occasions (e.g. in the cinema with
// Rick/Lisa/Gionnan, then rewatched at home with Joy years later) - a single movie-wide `cinema`
// flag can't tell those apart, so each person also gets their own "was this viewing in the
// cinema" flag, checked only when both that person AND the Cinema filter are selected.
const WATCHED_WITH_CINEMA_FIELD = {
  Joy: 'watched_with_joy_cinema',
  Rick: 'watched_with_rick_cinema',
  Lisa: 'watched_with_lisa_cinema',
  Gionnan: 'watched_with_gionnan_cinema',
  Youssra: 'watched_with_youssra_cinema',
};

const WITH_PEOPLE = [
  { field: 'watchedWithJoy', label: 'Joy <3' },
  { field: 'watchedWithRick', label: 'Rick' },
  { field: 'watchedWithLisa', label: 'Lisa' },
  { field: 'watchedWithGionnan', label: 'Gionnan' },
  { field: 'watchedWithYoussra', label: 'Youssra' },
];

const PERSON_CINEMA_FIELD = {
  watchedWithJoy: 'watchedWithJoyCinema',
  watchedWithRick: 'watchedWithRickCinema',
  watchedWithLisa: 'watchedWithLisaCinema',
  watchedWithGionnan: 'watchedWithGionnanCinema',
  watchedWithYoussra: 'watchedWithYoussraCinema',
};

function matchesCinema(movie, person) {
  if (person && WATCHED_WITH_CINEMA_FIELD[person]) {
    return movie[WATCHED_WITH_CINEMA_FIELD[person]] === true;
  }
  return movie.cinema === true || Object.values(WATCHED_WITH_CINEMA_FIELD).some((f) => movie[f] === true);
}

function App() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [originalMovies, setOriginalMovies] = useState([]);
  const [movies, setMovies] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [universeFilter, setUniverseFilter] = useState('');
  const [watchedWithFilter, setWatchedWithFilter] = useState('');
  const [cinemaFilter, setCinemaFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('original');

  const [showAddForm, setShowAddForm] = useState(false);

  const cardRefs = useRef([]);
  const downloadLongPress = useLongPress();
  const editLongPress = useLongPress();

  const [editingMovie, setEditingMovie] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editPoster, setEditPoster] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');

  const openEdit = (movie) => {
    setEditingMovie(movie);
    setEditForm({
      title: movie.title || '',
      rating: movie.rating || '',
      thoughts: movie.thoughts || '',
      marvel: movie.marvel || false,
      dc: movie.dc || false,
      watchedWithJoy: movie.watched_with_joy || false,
      watchedWithRick: movie.watched_with_rick || false,
      watchedWithLisa: movie.watched_with_lisa || false,
      watchedWithGionnan: movie.watched_with_gionnan || false,
      watchedWithYoussra: movie.watched_with_youssra || false,
      watchedWithJoyCinema: movie.watched_with_joy_cinema || false,
      watchedWithRickCinema: movie.watched_with_rick_cinema || false,
      watchedWithLisaCinema: movie.watched_with_lisa_cinema || false,
      watchedWithGionnanCinema: movie.watched_with_gionnan_cinema || false,
      watchedWithYoussraCinema: movie.watched_with_youssra_cinema || false,
      cinema: movie.cinema || false,
    });
    setEditPoster(null);
    setEditStatus('');
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (type === 'checkbox' && PERSON_CINEMA_FIELD[name]) {
        next[PERSON_CINEMA_FIELD[name]] = checked ? prev.cinema : false;
      }
      if (type === 'checkbox' && name === 'cinema') {
        WITH_PEOPLE.forEach(({ field }) => {
          if (prev[field]) next[PERSON_CINEMA_FIELD[field]] = checked;
        });
      }
      return next;
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.title || !editForm.rating) {
      setEditStatus('Please fill in a title and rating.');
      return;
    }
    setEditLoading(true);
    setEditStatus('');
    try {
      let newPosterUrl = null;
      if (editPoster) newPosterUrl = await uploadPoster(editPoster);

      const tagAtPatch = tagAtPatchOnEdit([
        { wasOn: !!editingMovie.watched_with_joy, isOn: editForm.watchedWithJoy, atField: 'watched_with_joy_at' },
        { wasOn: !!editingMovie.watched_with_rick, isOn: editForm.watchedWithRick, atField: 'watched_with_rick_at' },
        { wasOn: !!editingMovie.watched_with_lisa, isOn: editForm.watchedWithLisa, atField: 'watched_with_lisa_at' },
        { wasOn: !!editingMovie.watched_with_gionnan, isOn: editForm.watchedWithGionnan, atField: 'watched_with_gionnan_at' },
        { wasOn: !!editingMovie.watched_with_youssra, isOn: editForm.watchedWithYoussra, atField: 'watched_with_youssra_at' },
      ]);

      const patch = {
        title: editForm.title,
        rating: parseFloat(editForm.rating),
        marvel: editForm.marvel,
        dc: editForm.dc,
        watched_with_joy: editForm.watchedWithJoy,
        watched_with_rick: editForm.watchedWithRick,
        watched_with_lisa: editForm.watchedWithLisa,
        watched_with_gionnan: editForm.watchedWithGionnan,
        watched_with_youssra: editForm.watchedWithYoussra,
        watched_with_joy_cinema: editForm.watchedWithJoyCinema,
        watched_with_rick_cinema: editForm.watchedWithRickCinema,
        watched_with_lisa_cinema: editForm.watchedWithLisaCinema,
        watched_with_gionnan_cinema: editForm.watchedWithGionnanCinema,
        watched_with_youssra_cinema: editForm.watchedWithYoussraCinema,
        cinema: editForm.cinema,
        thoughts: editForm.thoughts || '',
        ...tagAtPatch,
      };
      if (newPosterUrl) patch.poster_url = newPosterUrl;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/movies?id=eq.${editingMovie.id}`, {
        method: 'PATCH',
        headers: supabaseHeaders(true),
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error('Update failed');

      const updatedMovie = {
        ...editingMovie,
        title: editForm.title,
        rating: parseFloat(editForm.rating),
        marvel: editForm.marvel,
        dc: editForm.dc,
        watched_with_joy: editForm.watchedWithJoy,
        watched_with_rick: editForm.watchedWithRick,
        watched_with_lisa: editForm.watchedWithLisa,
        watched_with_gionnan: editForm.watchedWithGionnan,
        watched_with_youssra: editForm.watchedWithYoussra,
        watched_with_joy_cinema: editForm.watchedWithJoyCinema,
        watched_with_rick_cinema: editForm.watchedWithRickCinema,
        watched_with_lisa_cinema: editForm.watchedWithLisaCinema,
        watched_with_gionnan_cinema: editForm.watchedWithGionnanCinema,
        watched_with_youssra_cinema: editForm.watchedWithYoussraCinema,
        cinema: editForm.cinema,
        thoughts: editForm.thoughts || '',
        ...tagAtPatch,
        ...(newPosterUrl ? { poster_url: newPosterUrl } : {}),
      };

      const updateList = (list) => list.map((m) => m.id === editingMovie.id ? updatedMovie : m);
      setOriginalMovies(updateList);
      setMovies(updateList);

      setEditStatus('Saved :D');
      setTimeout(() => setEditingMovie(null), 1000);
    } catch (err) {
      console.error(err);
      setEditStatus(`Something went wrong: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${editingMovie.title}"?`)) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/movies?id=eq.${editingMovie.id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      if (!res.ok) throw new Error('Delete failed');
      setOriginalMovies((prev) => prev.filter((m) => m.id !== editingMovie.id));
      setMovies((prev) => prev.filter((m) => m.id !== editingMovie.id));
      setEditingMovie(null);
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    }
  };

  const fetchMovies = useCallback(async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/movies?order=created_at.desc&select=*&limit=1000`, {
        headers: supabaseHeaders(false),
      });
      if (!res.ok) throw new Error('Failed to fetch movies');
      const allMovies = await res.json();
      setOriginalMovies(allMovies);
      setMovies(allMovies);
    } catch (err) {
      console.error('Failed to fetch movies:', err);
    }
  }, []);

  useEffect(() => {
    fetchMovies();
  }, [fetchMovies]);

  const applyFiltersAndSort = (opts = {}) => {
    const currentSearch = opts.searchTerm !== undefined ? opts.searchTerm : searchTerm;
    const currentRating = opts.ratingFilter !== undefined ? opts.ratingFilter : ratingFilter;
    const currentDate = opts.dateFilter !== undefined ? opts.dateFilter : dateFilter;
    const currentUniverse = opts.universeFilter !== undefined ? opts.universeFilter : universeFilter;
    const currentWatchedWith = opts.watchedWithFilter !== undefined ? opts.watchedWithFilter : watchedWithFilter;
    const currentCinema = opts.cinemaFilter !== undefined ? opts.cinemaFilter : cinemaFilter;
    const currentSort = opts.sortOrder !== undefined ? opts.sortOrder : sortOrder;

    let filtered = [...originalMovies];

    if (currentRating) {
      const min = parseFloat(currentRating);
      const max = min + 0.9;
      filtered = filtered.filter(
        (movie) => movie.rating >= min && movie.rating <= max
      );
    }

    if (currentSearch) {
      filtered = filtered.filter((movie) =>
        movie.title.toLowerCase().includes(currentSearch.toLowerCase())
      );
    }

    if (currentDate) {
      const now = new Date();
      const pastDate = new Date(now);
      pastDate.setDate(now.getDate() - parseInt(currentDate));
      filtered = filtered.filter(
        (movie) => new Date(movie.created_at) >= pastDate
      );
    }

    if (currentUniverse === 'marvel') {
      filtered = filtered.filter((movie) => movie.marvel === true);
    } else if (currentUniverse === 'dc') {
      filtered = filtered.filter((movie) => movie.dc === true);
    }

    if (currentCinema === 'true') {
      filtered = filtered.filter((movie) => matchesCinema(movie, currentWatchedWith));
    } else if (currentCinema === 'false') {
      filtered = filtered.filter((movie) => !matchesCinema(movie, currentWatchedWith));
    }

    if (currentWatchedWith && WATCHED_WITH_FIELD[currentWatchedWith]) {
      const field = WATCHED_WITH_FIELD[currentWatchedWith];
      filtered = filtered.filter((movie) => movie[field] === true);
    }

    filtered.sort(byTagAtOrCreatedAt(currentWatchedWith, WATCHED_AT_FIELD));

    if (currentSort === 'highest') {
      filtered.sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
    } else if (currentSort === 'lowest') {
      filtered.sort((a, b) => parseFloat(a.rating) - parseFloat(b.rating));
    }

    setMovies(filtered);
  };

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    applyFiltersAndSort({ searchTerm: val });
  };

  const onRatingChange = (e) => {
    const val = e.target.value;
    setRatingFilter(val);
    applyFiltersAndSort({ ratingFilter: val });
  };

  const onDateChange = (e) => {
    const val = e.target.value;
    setDateFilter(val);
    applyFiltersAndSort({ dateFilter: val });
  };

  const onUniverseChange = (e) => {
    const val = e.target.value;
    setUniverseFilter(val);
    applyFiltersAndSort({ universeFilter: val });
  };

  const onWatchedWithChange = (e) => {
    const val = e.target.value;
    setWatchedWithFilter(val);
    applyFiltersAndSort({ watchedWithFilter: val });
  };

  const toggleCinema = () => {
    const val = cinemaFilter === 'true' ? '' : 'true';
    setCinemaFilter(val);
    applyFiltersAndSort({ cinemaFilter: val });
  };

  const onSortChange = (order) => {
    setSortOrder(order);
    applyFiltersAndSort({ sortOrder: order });
  };

  const personOptions = availablePeople(originalMovies, (value) => WATCHED_WITH_FIELD[value]);

  return (
    <div className="app-container">
      <div className="header">
        <div className="header-title-row">
          <h1>Movie Reviews</h1>
          <button onClick={() => navigate('/todo-movies')} style={{ fontFamily: 'angelWish', fontSize: '4rem', letterSpacing: '0.06em', background: 'white', border: '2px solid black', borderRadius: '8px', padding: '0.2em 0.6em', cursor: 'pointer' }}>To-Do</button>
        </div>
        <div className="controls">
          {user && <button onClick={() => setShowAddForm(true)}>Add +</button>}
          <input
            type="text"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={onSearchChange}
          />
          <button onClick={() => onSortChange('highest')} className={sortOrder === 'highest' ? 'btn-active' : ''}>Highest Rating</button>
          <button onClick={() => onSortChange('lowest')} className={sortOrder === 'lowest' ? 'btn-active' : ''}>Lowest Rating</button>
          <button onClick={() => onSortChange('original')} className={sortOrder === 'original' ? 'btn-active' : ''}>Newest</button>

          <select value={ratingFilter} onChange={onRatingChange}>
            <option value="">Filter by Rating</option>
            {[...Array(10)].map((_, i) => (
              <option key={i} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>

          <select value={dateFilter} onChange={onDateChange}>
            <option value="">Filter by Date</option>
            <option value="7">Last Week</option>
            <option value="30">Last Month</option>
            <option value="180">Last 6 Months</option>
            <option value="365">Last Year</option>
          </select>

          <select value={universeFilter} onChange={onUniverseChange}>
            <option value="">Marvel/DC</option>
            <option value="marvel">Marvel</option>
            <option value="dc">DC</option>
          </select>

          <button onClick={toggleCinema} className={cinemaFilter === 'true' ? 'btn-active' : ''}>Cinema</button>

          <select value={watchedWithFilter} onChange={onWatchedWithChange}>
            <option value="">Filter by person</option>
            {personOptions.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="movies-container">
        {movies.map((movie, idx) => (
          <div
            className="movie-card"
            key={idx}
            ref={(el) => (cardRefs.current[idx] = el)}
          >
            {movie.poster_url && (
              <div className="movie-poster-wrap">
                <img
                  src={movie.poster_url}
                  alt={`${movie.title} Poster`}
                  className="movie-poster"
                  loading="lazy"
                  onDoubleClick={user ? () => downloadCardImage(cardRefs.current[idx], movie.title) : undefined}
                  onTouchStart={user ? () => downloadLongPress.start(() => downloadCardImage(cardRefs.current[idx], movie.title)) : undefined}
                  onTouchEnd={downloadLongPress.cancel}
                  onTouchCancel={downloadLongPress.cancel}
                  crossOrigin="anonymous"
                />
              </div>
            )}

            <div className="movie-content">
              <div className="movie-header">
                <h2>{movie.title}</h2>
                <span
                  className="movie-rating"
                  onDoubleClick={user ? () => openEdit(movie) : undefined}
                  onTouchStart={user ? () => editLongPress.start(() => openEdit(movie)) : undefined}
                  onTouchEnd={editLongPress.cancel}
                  onTouchCancel={editLongPress.cancel}
                >{Number(movie.rating).toFixed(1)}</span>
              </div>
              <div className="movie-thoughts">
                {movie.thoughts || '*'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {movies.length === 0 && <p className="no-results">Geen ene tering hier man.</p>}

      {showAddForm && (
        <Modal onClose={() => setShowAddForm(false)}>
          <MovieForm onClose={() => { setShowAddForm(false); fetchMovies(); }} />
        </Modal>
      )}

      {editingMovie && (
        <Modal onClose={() => setEditingMovie(null)}>
          <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <h2 style={{ fontFamily: 'angelWish', fontSize: '2.5rem', letterSpacing: '0.06em', margin: '0 0 1rem 0' }}>Edit Movie</h2>
            <div className="form-group">
              <label>Title *</label>
              <input type="text" name="title" value={editForm.title} onChange={handleEditChange} required />
            </div>
            <div className="form-group">
              <label>Rating *</label>
              <input type="number" name="rating" value={editForm.rating} onChange={handleEditChange} min="1" max="10" step="0.1" required />
            </div>
            <div className="form-group">
              <label>Thoughts</label>
              <textarea name="thoughts" value={editForm.thoughts} onChange={handleEditChange} rows={4} />
            </div>
            <div className="form-group">
              <label>Poster</label>
              {editingMovie?.poster_url && !editPoster && (
                <img src={editingMovie.poster_url} alt="poster" style={{ width: 80, borderRadius: 4, marginBottom: '0.4rem' }} />
              )}
              <input type="file" accept="image/*" onChange={(e) => setEditPoster(e.target.files[0])} />
            </div>
            <div className="form-group">
              <label>Marvel/DC</label>
              <div className="form-checkboxes">
                <label><input type="checkbox" name="marvel" checked={editForm.marvel} onChange={handleEditChange} /> Marvel</label>
                <label><input type="checkbox" name="dc" checked={editForm.dc} onChange={handleEditChange} /> DC</label>
              </div>
            </div>
            <div className="form-group">
              <label>Cinema</label>
              <div className="form-checkboxes">
                <input type="checkbox" name="cinema" checked={editForm.cinema} onChange={handleEditChange} />
              </div>
            </div>
            <div className="form-group">
              <label>With</label>
              <div className="form-checkboxes form-checkboxes--people">
                {WITH_PEOPLE.map(({ field, label }) => (
                  <div className="with-person-row" key={field}>
                    <label><input type="checkbox" name={field} checked={editForm[field]} onChange={handleEditChange} /> {label}</label>
                    {editForm.cinema && (
                      <label className="cinema-subcheck">
                        <input
                          type="checkbox"
                          name={PERSON_CINEMA_FIELD[field]}
                          checked={editForm[PERSON_CINEMA_FIELD[field]]}
                          disabled={!editForm[field]}
                          onChange={handleEditChange}
                        /> C
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="edit-modal-actions">
              <button type="button" className="form-submit" style={{ background: '#eee', color: '#333', borderColor: '#999', WebkitTextFillColor: '#333' }} onClick={() => setEditingMovie(null)}>Cancel</button>
              <button type="button" className="form-submit btn-delete" onClick={handleDelete}>Delete</button>
              <button type="submit" className="form-submit" disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
            </div>
            {editStatus && <p className="form-status">{editStatus}</p>}
          </form>
        </Modal>
      )}
    </div>
  );
}

export default App;
